/**
 * ============================================================================
 * AuditCalendar — Phase 7 frontend
 * Owner: Phase 7 — Internal Audit Programme Module.
 * Route: /internal-audits
 * Purpose: Annual audit-plan summary + 12-month calendar visualisation of
 *          scheduled audits. Clicking an audit chip navigates to AuditDetail.
 *
 * UX rules (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Empty state has a primary CTA + governance copy.
 *   - Loading state shows skeleton rows (no spinner-only).
 *   - Error envelope with Retry button — never an uncaught toast.
 *   - v2 visual tokens; RTL-friendly via logical CSS properties.
 *
 * Cross-module links (clickable):
 *   - chip → /internal-audits/$id (AuditDetail)
 *   - audit number → AuditDetail
 *
 * URS rows: INA-001, INA-002, INA-003.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@edms/design-system';
import styles from './AuditCalendar.module.css';
import type { AuditListItem, AuditStatus } from './types';
import { useAuditPlansQuery } from './useAuditPlansQuery';
import { useAuditsQuery } from './useAuditsQuery';
import { NewAuditPlanDialog } from './NewAuditPlanDialog';

const MONTHS_EN = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function monthIndex(iso: string): number {
  // Cheap parse so we don't depend on the user's locale calendar shift.
  // ISO-8601 is always YYYY-MM-DDTHH:mm:ssZ; month is chars 5-7.
  const m = Number(iso.substring(5, 7));
  return Number.isFinite(m) ? m - 1 : 0;
}

export interface AuditCalendarProps {
  /** Override the calendar year (defaults to current year). */
  initialYear?: number;
  /** Navigation hook the host injects (router-agnostic). */
  onNavigateAudit?: (auditId: string) => void;
  /** Optional callback for the "New Plan" button (host-provided). */
  onNewPlan?: () => void;
}

export function AuditCalendar({
  initialYear,
  onNavigateAudit,
  onNewPlan,
}: AuditCalendarProps) {
  const { t } = useTranslation();
  const thisYear = initialYear ?? new Date().getUTCFullYear();
  const [year, setYear] = useState<number>(thisYear);
  const [showNewPlan, setShowNewPlan] = useState(false);
  const plansQ = useAuditPlansQuery();
  const auditsQ = useAuditsQuery({ year });

  // The button is always actionable: a host may inject its own handler, but by
  // default it opens the built-in create dialog (drafts/61 F0-8).
  const handleNewPlan = onNewPlan ?? (() => setShowNewPlan(true));

  const auditsByMonth = useMemo(() => {
    const buckets: AuditListItem[][] = Array.from({ length: 12 }, () => []);
    const items = auditsQ.data?.items ?? [];
    for (const a of items) {
      buckets[monthIndex(a.planned_start_utc)]?.push(a);
    }
    return buckets;
  }, [auditsQ.data]);

  const yearRange = useMemo(() => {
    const base = thisYear;
    return [base - 1, base, base + 1];
  }, [thisYear]);

  const planRows = plansQ.data?.items ?? [];
  const isLoadingAudits = auditsQ.isLoading;
  const isErrorAudits = auditsQ.isError;
  const isEmpty = !isLoadingAudits && !isErrorAudits && (auditsQ.data?.items ?? []).length === 0;

  return (
    <div className={styles.screen} data-testid="audit-calendar-screen">
      <PageHero
        eyebrow={t('internalAudit.calendar.eyebrow', {
          defaultValue: 'Quality Events',
        })}
        title={t('internalAudit.calendar.title', {
          defaultValue: 'Internal Audit Programme',
        })}
        subtitle={t('internalAudit.calendar.subtitle', {
          defaultValue:
            'Annual audit plan + scheduled audits visualised over the calendar year. ICH Q10 §II.F + EU GMP Ch.9.',
        })}
        actions={
          <div className={styles.toolbar}>
          <label htmlFor="ina-year-select" style={{ fontSize: '0.875rem' }}>
            {t('internalAudit.calendar.year', { defaultValue: 'Year' })}
          </label>
          <select
            id="ina-year-select"
            className={styles.yearSelect}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            aria-label={t('internalAudit.calendar.yearAria', {
              defaultValue: 'Select calendar year',
            })}
          >
            {yearRange.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleNewPlan}
            style={{
              padding: '0.5rem 0.75rem',
              background: 'var(--color-primary, #1d4ed8)',
              color: '#fff',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
            }}
            data-testid="ina-new-plan-btn"
          >
            {t('internalAudit.calendar.newPlan', {
              defaultValue: '+ New Annual Plan',
            })}
          </button>
          </div>
        }
      />

      {/* Annual plan summary list */}
      <section aria-labelledby="ina-plans-heading">
        <h2 id="ina-plans-heading" style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>
          {t('internalAudit.calendar.plans', { defaultValue: 'Annual Plans' })}
        </h2>
        {plansQ.isLoading ? (
          <div role="status" aria-live="polite">
            {[0, 1].map((i) => (
              <div key={i} className={styles.skeletonRow} />
            ))}
          </div>
        ) : plansQ.isError ? (
          <div className={styles.empty} role="alert">
            {t('internalAudit.calendar.plansError', {
              defaultValue: 'Could not load audit plans.',
            })}
          </div>
        ) : planRows.length === 0 ? (
          <div className={styles.empty}>
            {t('internalAudit.calendar.plansEmpty', {
              defaultValue:
                'No annual plans yet. Create one to start scheduling audits.',
            })}
          </div>
        ) : (
          <ul className={styles.planList} data-testid="ina-plans-list">
            {planRows.map((p) => (
              <li key={p.plan_id} className={styles.planRow}>
                <div className={styles.planMeta}>
                  <span className={styles.planTitle}>{p.title}</span>
                  <span className={styles.planSub}>
                    {t('internalAudit.calendar.planYear', { defaultValue: 'Plan year' })}
                    {' '}: {p.plan_year}
                  </span>
                </div>
                <span className={styles.statusPill} data-status={p.status}>
                  {p.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 12-month calendar */}
      <section aria-labelledby="ina-calendar-heading">
        <h2 id="ina-calendar-heading" style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>
          {t('internalAudit.calendar.scheduled', {
            defaultValue: 'Scheduled audits',
          })}{' '}
          ({year})
        </h2>
        {isLoadingAudits ? (
          <div role="status" aria-live="polite">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={styles.skeletonRow} />
            ))}
          </div>
        ) : isErrorAudits ? (
          <div className={styles.empty} role="alert">
            {t('internalAudit.calendar.error', {
              defaultValue: 'Could not load audits. Try again.',
            })}
            <div>
              <button
                type="button"
                onClick={() => auditsQ.refetch()}
                style={{ marginTop: '0.5rem' }}
              >
                {t('common.retry', { defaultValue: 'Retry' })}
              </button>
            </div>
          </div>
        ) : isEmpty ? (
          <div className={styles.empty}>
            {t('internalAudit.calendar.empty', {
              defaultValue:
                'No audits scheduled for this year yet. Approve a plan and schedule the first audit.',
            })}
          </div>
        ) : (
          <div className={styles.calendarGrid} data-testid="ina-calendar-grid">
            {MONTHS_EN.map((label, monthIdx) => (
              <div key={label} className={styles.monthCol}>
                <div className={styles.monthLabel}>{label}</div>
                {(auditsByMonth[monthIdx] ?? []).map((a) => (
                  <button
                    key={a.audit_id}
                    type="button"
                    className={styles.eventChip}
                    data-variance={String(a.variance)}
                    data-testid={`ina-audit-chip-${a.audit_number}`}
                    onClick={() => onNavigateAudit?.(a.audit_id)}
                    aria-label={t('internalAudit.calendar.openAudit', {
                      defaultValue: 'Open audit {{auditNumber}}',
                      auditNumber: a.audit_number,
                    })}
                  >
                    <span className={styles.eventNumber}>{a.audit_number}</span>
                    <span className={styles.eventScope}>{a.audit_scope}</span>
                    <span className={styles.statusPill} data-status={a.status}>
                      {auditStatusLabel(a.status, t as unknown as (k: string, opts?: object) => string)}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </section>

      {showNewPlan && (
        <NewAuditPlanDialog
          onClose={() => setShowNewPlan(false)}
          onCreated={() => {
            setShowNewPlan(false);
            void plansQ.refetch();
          }}
        />
      )}
    </div>
  );
}

function auditStatusLabel(s: AuditStatus, t: (k: string, opts?: object) => string) {
  switch (s) {
    case 'PLANNED':
      return t('internalAudit.status.PLANNED', { defaultValue: 'Planned' });
    case 'IN_PROGRESS':
      return t('internalAudit.status.IN_PROGRESS', { defaultValue: 'In progress' });
    case 'REPORT_DRAFT':
      return t('internalAudit.status.REPORT_DRAFT', { defaultValue: 'Report draft' });
    case 'REPORT_FINALIZED':
      return t('internalAudit.status.REPORT_FINALIZED', { defaultValue: 'Report final' });
    case 'CLOSED':
      return t('internalAudit.status.CLOSED', { defaultValue: 'Closed' });
    case 'CANCELLED':
      return t('internalAudit.status.CANCELLED', { defaultValue: 'Cancelled' });
    default:
      return s;
  }
}
